import { ArrowUpRight } from 'lucide-react'

import { MetalTierBadge } from 'components/member_onboarding_v2/components/MetalTierBadge/MetalTierBadge'
import { PlanBadge } from 'components/member_onboarding_v2/components/PlanBadge/PlanBadge'
import { PlanCard } from 'components/member_onboarding_v2/components/PlanCard/PlanCard'
import { PlanChip } from 'components/member_onboarding_v2/components/PlanChip'
import { extractInNetworkAmount, extractInNetworkString, getDeductibleTooltipLabel, MOOP_INFO, PRIMARY_DOCTOR_VISITS_INFO, SPECIALIST_DOCTOR_VISITS_INFO } from 'components/member_onboarding_v2/utils'
import { UnderlinedTooltip } from 'components/ui/UnderlinedTooltip'
import { ButtonLink, SolidAlertCircleIcon, SolidCircleCheckIcon, useBreakpoint } from 'slab'
import { formatMoney, getFirstName } from 'utils'
import { planLink } from 'utils/quote_calculations'
import { type MemberOnboardingMedicalPlanDisplayItem, type QuotingQuoteResult } from 'utils/web_api.schemas'
interface EmployeePlanCardProps {
  plan: MemberOnboardingMedicalPlanDisplayItem
  isIndividual: boolean
  employee: QuotingQuoteResult
  usingProjectedData?: boolean
  monthlyAllowance: number
  quoteYear: number | null
}

export const EmployeePlanCard = ({
  plan,
  isIndividual,
  employee,
  usingProjectedData = false,
  monthlyAllowance,
  quoteYear,
}: EmployeePlanCardProps) => {
  const { breakpointIsAtLeast } = useBreakpoint()
  const currentYear = new Date().getFullYear()

  const premium = Number(((plan.total_premium.subunits || 0) / 100).toFixed(2))
  const isBestFit = employee.medical_plan?.id === plan.plan.id
  const isFullyCovered = monthlyAllowance - premium >= 0
  const employeeName = getFirstName(employee.name) || employee.name

  const parsedDeductible = extractInNetworkAmount(
    isIndividual
      ? plan.plan.individual_medical_deductible
      : plan.plan.family_medical_deductible
  )
  const deductibleValue =
    typeof parsedDeductible === 'string'
      ? parsedDeductible
      : formatMoney(parsedDeductible)

  const parsedMOOP = extractInNetworkAmount(
    isIndividual ? plan.plan.individual_medical_moop : plan.plan.family_medical_moop
  )

  return (
    <div>
      {isBestFit && (
        <div className='w-fit leading-[18px] rounded-tl-[9px] rounded-tr-[9px] bg-violet-100 text-violet-500 border border-violet-300 body-sm-bold px-3 py-2 ml-2'>
          Best fit for <span className='sensitive'>{employeeName}</span>
        </div>
      )}
      <PlanCard layout={breakpointIsAtLeast('md') ? 'horizontal' : 'vertical'}>
        <PlanCard.GraySection wideMode>
          <div className='flex flex-col gap-4 h-full'>
            <PlanChip
              carrierLogo={plan.plan.carrier_image_url ?? ''}
              carrierName={plan.plan.carrier_name}
              planName={plan.plan.friendly_name}
            />
            <div className='flex flex-wrap gap-2'>
              <MetalTierBadge bordered tier={plan.plan.metal_tier} />
              <PlanBadge bordered type={plan.plan.plan_type} />
              {isFullyCovered && (
                <div className='bg-green-50 text-green-500 border border-green-300 body-sm-bold px-2 py-1 rounded-lg'>
                  Fully covered by allowance
                </div>
              )}
            </div>
            {/* Confirmed plan for the current year */}
            {!usingProjectedData && quoteYear === currentYear && (
              <div className='mt-auto'>
                <ButtonLink
                  href={planLink(plan.plan.id, premium)}
                  target='_blank'
                  variant='tertiary-deemphasized'
                >
                  <div className='flex items-center gap-1'>
                    <span className='text-thatch-500'>View full plan details</span>
                    <ArrowUpRight className='w-4 h-4 text-thatch-500' />
                  </div>
                </ButtonLink>
              </div>
            )}
            {/* Confirmed plan for the coming year */}
            {!usingProjectedData && quoteYear !== currentYear && (
              <div className='mt-auto'>
                <UnderlinedTooltip content={`This plan has been confirmed by ${plan.plan.carrier_name} to be available in ${quoteYear}`} withPortal={false}>
                  <div className='flex items-center gap-1 cursor-help'>
                    <SolidCircleCheckIcon className='w-4 h-4 text-green-400' />
                    <span className='body-bold text-green-500'>
                      Confirmed {quoteYear} plan
                    </span>
                  </div>
                </UnderlinedTooltip>
              </div>
            )}
            {/* Estimated plan for the coming year based on current year plans plus expected rate increases */}
            {usingProjectedData && (
              <div className='mt-auto'>
                <UnderlinedTooltip content={`This plan is similar to one that existed in ${currentYear} but final availability, costs, and details have not yet been released by the insurance company`} withPortal={false}>
                  <div className='flex items-center gap-1 cursor-help'>
                    <SolidAlertCircleIcon className='w-4 h-4 text-yellow-400' />
                    <span className='body-bold text-yellow-500'>
                      Example {quoteYear} plan
                    </span>
                  </div>
                </UnderlinedTooltip>
              </div>
            )}
          </div>
        </PlanCard.GraySection>
        <PlanCard.Section>
          <div className='flex flex-col gap-6 h-full'>
            <div className='flex flex-col xl:flex-row gap-6 h-full justify-between'>
              <PlanCard.Features>
                <PlanCard.Feature
                  detail={deductibleValue}
                  term={isIndividual ? 'Deductible' : 'Family deductible'}
                  info={getDeductibleTooltipLabel(
                    parsedDeductible,
                    isIndividual
                  )}
                />
                <PlanCard.Feature
                  detail={
                      typeof parsedMOOP === 'string'
                        ? parsedMOOP
                        : formatMoney(parsedMOOP)
                    }
                  term={
                      isIndividual
                        ? 'Out-of-pocket max'
                        : 'Family out-of-pocket max'
                    }
                  info={MOOP_INFO}
                />
                <PlanCard.Feature
                  detail={extractInNetworkString(
                    plan.plan.primary_care_physician_benefit
                  )}
                  term='Primary doctor visits'
                  info={PRIMARY_DOCTOR_VISITS_INFO}
                />
                <PlanCard.Feature
                  detail={extractInNetworkString(plan.plan.specialist_benefit)}
                  term='Specialist doctor visits'
                  info={SPECIALIST_DOCTOR_VISITS_INFO}
                />
              </PlanCard.Features>
              <PlanCard.Premium
                align={breakpointIsAtLeast('md') ? 'right' : 'left'}
                amount={premium}
                remainder={monthlyAllowance - premium}
                displayMode='large'
                audience='employer'
              />
            </div>
          </div>
        </PlanCard.Section>
      </PlanCard>
    </div>
  )
}
